import React, { useState, useEffect } from "react";
import { Save, User, Shield, Loader2, LogIn, Rocket, SlidersHorizontal, Filter } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/Card";
import { SettingsBanner } from "../../components/ui/SettingsBanner";
import { ToggleSwitch } from "../../components/ui/ToggleSwitch";
import { useAuth } from "../../hooks/useAuth";
import { useToggleableSetting } from "../../hooks/useToggleableSetting";
import { authApi, settingsApi } from "../../utils/api";
import OnboardingSuggestions from "./OnboardingSuggestions";
import CitySourceToggles from "./CitySourceToggles";
import TicketProviders from "./TicketProviders";
import LocalizedContent from "./LocalizedContent";

const LOGIN_METHODS = [
  { key: "spotify", label: "Spotify", description: "Spotify OAuth button" },
  { key: "google", label: "YouTube Music (Google)", description: "Google / YouTube Music OAuth button" },
  { key: "email", label: "Email & Password", description: "Email/password sign-in and sign-up form" },
];

export default function SettingsPage() {
  const { user } = useAuth();

  const [passwordData, setPasswordData] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState(null);
  const [passwordError, setPasswordError] = useState(null);

  // Login method visibility (consumer web app)
  const loginMethods = useToggleableSetting(settingsApi.getLoginMethods, settingsApi.updateLoginMethods, {
    loadErrorMessage: "Failed to load login method settings",
    updateErrorMessage: "Failed to update login methods",
    successMessage: "Login methods updated",
  });

  // Event-promotion settings
  const promotionSettings = useToggleableSetting(
    settingsApi.getPromotionSettings,
    settingsApi.updatePromotionSettings,
    {
      loadErrorMessage: "Failed to load promotion settings",
      updateErrorMessage: "Failed to update promotion settings",
      successMessage: "Promotion settings updated",
    }
  );

  const classificationSettings = useToggleableSetting(
    settingsApi.getClassificationSettings,
    settingsApi.updateClassificationSettings,
    {
      loadErrorMessage: "Failed to load classification settings",
      updateErrorMessage: "Failed to update classification settings",
      successMessage: "Classification settings updated",
    }
  );

  const occurrenceDedupSettings = useToggleableSetting(
    settingsApi.getOccurrenceDedupSettings,
    settingsApi.updateOccurrenceDedupSettings,
    {
      loadErrorMessage: "Failed to load occurrence dedup settings",
      updateErrorMessage: "Failed to update occurrence dedup settings",
      successMessage: "Occurrence dedup settings updated",
    }
  );

  // OpenData Madrid ingestion filter
  const madridSettings = useToggleableSetting(
    settingsApi.getOpenDataMadridSettings,
    settingsApi.updateOpenDataMadridSettings,
    {
      loadErrorMessage: "Failed to load OpenData Madrid settings",
      updateErrorMessage: "Failed to update OpenData Madrid settings",
      successMessage: "OpenData Madrid settings updated",
    }
  );

  // Link-dedup providers, edited as a comma-separated draft like the Madrid categories below.
  const dedupProvidersValue = occurrenceDedupSettings.value?.dedup_providers;
  const dedupProvidersSaved = (dedupProvidersValue ?? []).join(", ");
  const [dedupProvidersDraft, setDedupProvidersDraft] = useState("");
  // Keyed on the array, not its joined text: the server normalizes a save ("Ticketmaster" →
  // "ticketmaster"), and when that lands on the saved text unchanged the draft must still reset.
  useEffect(() => {
    setDedupProvidersDraft((dedupProvidersValue ?? []).join(", "));
  }, [dedupProvidersValue]);
  const dedupProvidersDirty = dedupProvidersDraft !== dedupProvidersSaved;

  // Local draft so typing doesn't fire a request per keystroke.
  const [madridTypesDraft, setMadridTypesDraft] = useState("");
  const madridLoaded = madridSettings.value?.include_types;
  useEffect(() => {
    setMadridTypesDraft(madridLoaded ?? "");
  }, [madridLoaded]);

  const madridTypesDirty = madridTypesDraft !== (madridSettings.value?.include_types ?? "");

  // Tokens the admin has typed/picked, compared case-insensitively like the backend does.
  const madridTokens = madridTypesDraft.split(",").map((t) => t.trim()).filter(Boolean);
  const madridSelected = new Set(madridTokens.map((t) => t.toLowerCase()));

  const toggleMadridCategory = (category) => {
    const next = madridSelected.has(category.toLowerCase())
      ? madridTokens.filter((t) => t.toLowerCase() !== category.toLowerCase())
      : [...madridTokens, category];
    setMadridTypesDraft(next.join(", "));
  };

  useEffect(() => {
    if (!passwordMessage) return;
    const t = setTimeout(() => setPasswordMessage(null), 4000);
    return () => clearTimeout(t);
  }, [passwordMessage]);

  useEffect(() => {
    if (!passwordError) return;
    const t = setTimeout(() => setPasswordError(null), 4000);
    return () => clearTimeout(t);
  }, [passwordError]);

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSavePassword = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError("Passwords do not match");
      return;
    }
    if (passwordData.newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters long");
      return;
    }
    setPasswordLoading(true);
    setPasswordError(null);
    setPasswordMessage(null);
    try {
      await authApi.changePassword(passwordData.currentPassword, passwordData.newPassword);
      setPasswordMessage("Password updated successfully");
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (err) {
      setPasswordError(err.response?.data?.detail || "Error changing password");
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Settings</h2>
        <p className="text-muted-foreground">Manage your account and password</p>
      </div>

      {/* grid-cols-1, not bare grid: implicit tracks min-size to nowrap rows and overflow */}
      <div className="grid grid-cols-1 gap-6">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <User className="h-5 w-5 text-muted-foreground" />
              <CardTitle>Profile</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 text-sm">
              <div>
                <p className="text-muted-foreground">Name</p>
                <p className="font-medium">{user?.display_name || "-"}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Email</p>
                <p className="font-medium">{user?.email || "-"}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Role</p>
                <p className="font-medium">{user?.role || "-"}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-muted-foreground" />
              <CardTitle>Security</CardTitle>
            </div>
            <CardDescription>Update your password</CardDescription>
          </CardHeader>
          <CardContent>
            <SettingsBanner message={passwordMessage} error={passwordError} />
            <form onSubmit={handleSavePassword} className="space-y-4">
              <Input
                label="Current Password"
                name="currentPassword"
                type="password"
                value={passwordData.currentPassword}
                onChange={handlePasswordChange}
              />
              <div className="grid gap-4 md:grid-cols-2">
                <Input
                  label="New Password"
                  name="newPassword"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                />
                <Input
                  label="Confirm Password"
                  name="confirmPassword"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                />
              </div>
              <Button type="submit" disabled={passwordLoading}>
                {passwordLoading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Updating...</>
                ) : (
                  <><Save className="mr-2 h-4 w-4" />Update Password</>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <LogIn className="h-5 w-5 text-muted-foreground" />
              <CardTitle>Login Methods</CardTitle>
            </div>
            <CardDescription>
              Control which login options appear on the consumer web app's login page. This affects the
              consumer site only — your admin dashboard login is never changed.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SettingsBanner message={loginMethods.message} error={loginMethods.error} />
            {loginMethods.loading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />Loading...
              </div>
            ) : loginMethods.value ? (
              <div className="space-y-3">
                {LOGIN_METHODS.map(({ key, label, description }) => (
                  <ToggleSwitch
                    key={key}
                    label={label}
                    description={description}
                    checked={loginMethods.value[key]}
                    disabled={loginMethods.saving}
                    onToggle={() => loginMethods.toggle(key)}
                  />
                ))}
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Rocket className="h-5 w-5 text-muted-foreground" />
              <CardTitle>Event Promotion</CardTitle>
            </div>
            <CardDescription>
              Controls whether pending events without a classification are promoted to active.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SettingsBanner message={promotionSettings.message} error={promotionSettings.error} />
            {promotionSettings.loading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />Loading...
              </div>
            ) : promotionSettings.value ? (
              <ToggleSwitch
                label="Promote unclassified events"
                description="When event classification is enabled, promote pending events to active even without a classification (e.g. during an OpenAI outage) instead of holding them pending indefinitely."
                checked={promotionSettings.value.promote_unclassified_events}
                disabled={promotionSettings.saving}
                onToggle={() => promotionSettings.toggle("promote_unclassified_events")}
              />
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="h-5 w-5 text-muted-foreground" />
              <CardTitle>Classification Gates</CardTitle>
            </div>
            <CardDescription>
              Controls whether event classification waits for possible artist duplicate review on single-artist events.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SettingsBanner message={classificationSettings.message} error={classificationSettings.error} />
            {classificationSettings.loading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />Loading...
              </div>
            ) : classificationSettings.value ? (
              <ToggleSwitch
                label="Block single-artist possible duplicates"
                description="When enabled, event classification skips events with exactly one artist when that artist is in the possible duplicate review queue."
                checked={classificationSettings.value.block_events_with_possible_duplicate_artists}
                disabled={classificationSettings.saving}
                onToggle={() => classificationSettings.toggle("block_events_with_possible_duplicate_artists")}
              />
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="h-5 w-5 text-muted-foreground" />
              <CardTitle>Repeated Occurrences</CardTitle>
            </div>
            <CardDescription>
              Controls whether the consumer app hides repeated ticket entries and links for the same showing. Only
              affects what is shown — first-seen dates keep being recorded either way.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SettingsBanner message={occurrenceDedupSettings.message} error={occurrenceDedupSettings.error} />
            {occurrenceDedupSettings.loading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />Loading...
              </div>
            ) : occurrenceDedupSettings.value ? (
              <div className="space-y-4">
                <ToggleSwitch
                  label="Keep only the newest per showing"
                  description="For the providers below: of occurrences sharing a date, time and name, and of each provider's links, show only the most recently first-seen one. Takes effect within about a minute."
                  checked={occurrenceDedupSettings.value.enabled}
                  disabled={occurrenceDedupSettings.saving}
                  onToggle={() => occurrenceDedupSettings.toggle("enabled")}
                />
                <div className="space-y-2">
                  <Input
                    label="Providers"
                    name="dedup_providers"
                    value={dedupProvidersDraft}
                    placeholder="Leave empty to never hide anything"
                    disabled={occurrenceDedupSettings.saving}
                    onChange={(e) => setDedupProvidersDraft(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Comma-separated provider ids, as shown on ticket links (e.g. ticketmaster) — providers known to
                    relist the same showing under new links. Everything else is left alone: a provider is taken from
                    the link's site, so a venue's info page and its booking page would count as the same one.
                  </p>
                  <Button
                    type="button"
                    disabled={occurrenceDedupSettings.saving || !dedupProvidersDirty}
                    onClick={() =>
                      occurrenceDedupSettings.save({
                        dedup_providers: dedupProvidersDraft.split(",").map((p) => p.trim()).filter(Boolean),
                      })
                    }
                  >
                    {occurrenceDedupSettings.saving ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
                    ) : (
                      <><Save className="mr-2 h-4 w-4" />Save Providers</>
                    )}
                  </Button>
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-muted-foreground" />
              <CardTitle>OpenData Madrid</CardTitle>
            </div>
            <CardDescription>
              Controls which categories of the Madrid city open-data feed are ingested. Applied on the
              next fetch — no redeploy needed. Enable the source itself per city under Ingestion Sources.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SettingsBanner message={madridSettings.message} error={madridSettings.error} />
            {madridSettings.loading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />Loading...
              </div>
            ) : madridSettings.value ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Input
                    label="Categories"
                    name="include_types"
                    value={madridTypesDraft}
                    placeholder="Leave empty to ingest every category"
                    disabled={madridSettings.saving}
                    onChange={(e) => setMadridTypesDraft(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Comma-separated. Matching ignores case and accents, and works on any part of the
                    feed's category path — <span className="font-medium">Flamenco</span> selects{" "}
                    <span className="font-medium">DanzaBaile/Flamenco</span>, while{" "}
                    <span className="font-medium">DanzaBaile</span> selects that whole branch.
                  </p>

                  {madridSettings.value.known_categories?.length ? (
                    <div className="space-y-1.5">
                      <p className="text-xs text-muted-foreground">
                        Click to add or remove. These are the categories seen in the feed — you can
                        also type one that isn't listed, since the city publishes new ones over time.
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {madridSettings.value.known_categories.map((category) => {
                          const selected = madridSelected.has(category.toLowerCase());
                          return (
                            <button
                              key={category}
                              type="button"
                              disabled={madridSettings.saving}
                              aria-pressed={selected}
                              onClick={() => toggleMadridCategory(category)}
                              className={
                                "rounded-full border px-2.5 py-1 text-xs transition-colors disabled:opacity-50 " +
                                (selected
                                  ? "border-primary bg-primary text-primary-foreground"
                                  : "border-input bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground")
                              }
                            >
                              {category}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}

                  <Button
                    type="button"
                    disabled={madridSettings.saving || !madridTypesDirty}
                    onClick={() => madridSettings.save({ include_types: madridTypesDraft.trim() })}
                  >
                    {madridSettings.saving ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
                    ) : (
                      <><Save className="mr-2 h-4 w-4" />Save Categories</>
                    )}
                  </Button>
                </div>

                <ToggleSwitch
                  label="Ingest uncategorised events"
                  description="About 16% of feed records carry no category at all. They are always ingested when the field above is empty; with a filter set they are dropped unless this is on."
                  checked={madridSettings.value.include_untyped}
                  disabled={madridSettings.saving || !madridSettings.value.include_types}
                  onToggle={() => madridSettings.toggle("include_untyped")}
                />
                {!madridSettings.value.include_types ? (
                  <p className="text-xs text-muted-foreground">
                    No category filter is set, so every event is ingested and this toggle has no effect.
                  </p>
                ) : null}
              </div>
            ) : null}
          </CardContent>
        </Card>

        <CitySourceToggles />

        <TicketProviders />

        <LocalizedContent />

        <OnboardingSuggestions />
      </div>
    </div>
  );
}
